/**
 * PinterestDuplicatesModal — modal "Détecter doublons" sur
 * `<WorkPublicationsTab>` (S7-PR2).
 *
 * Cas d'usage : Stéphanie a accidentellement publié plusieurs Pins
 * identiques sur le même board (clic répété, retry, etc.). Le modal
 * groupe les Pins Pinterest `state='published'` par triplet
 * `(board_id, draft_version_label, published_at bucket 60s)` et propose
 * de marquer comme supprimés tous sauf le plus ancien de chaque groupe.
 *
 * Comportement :
 *  - Pre-sélection : le plus ancien (chronologique) de chaque groupe est
 *    gardé. Les suivants sont pré-cochés pour suppression.
 *  - L'utilisatrice peut décocher manuellement. Pas de bouton "tout
 *    sélectionner" / "tout décocher" — au-delà de 5-10 Pins doublons,
 *    on est probablement face à un autre type de problème (mass-publish
 *    raté) qui mérite une investigation, pas un nettoyage bulk.
 *  - Confirm `useConfirm` avant l'action (mutation irréversible côté DB
 *    même si Pinterest n'est pas appelé).
 *
 * **Local-only** : la mutation appelle l'endpoint
 * `POST /api/works/{id}/publications/pinterest/mark-duplicates-deleted`
 * qui bascule `state='deleted'` côté DB SANS appeler l'API Pinterest.
 * Stéphanie peut supprimer manuellement sur pinterest.com si elle veut
 * (cf. wording dans le footer informatif du modal).
 *
 * Helpers (`groupPinterestDuplicates`, `buildInitialDuplicateSelection`,
 * `countPinterestDuplicates`) vivent dans `pinterest-duplicates.ts` pour
 * respecter `react-refresh/only-export-components` (le fichier composant
 * n'exporte que des composants).
 */

import { useMemo, useState } from 'react'
import { toast } from 'sonner'

import Button from '@/components/ui/Button'
import Checkbox from '@/components/ui/Checkbox'
import Modal from '@/components/ui/Modal'
import { useConfirm } from '@/contexts/confirm-dialog-context'
import { useMarkPinterestDuplicatesDeleted } from '@/hooks/useWorkPublications'
import type { PublicationJournalEntry } from '@/hooks/useWorkPublicationsJournal'

import {
  buildInitialDuplicateSelection,
  groupPinterestDuplicates,
  type DuplicateGroup,
} from './pinterest-duplicates'
import './PinterestDuplicatesModal.css'

interface PinterestDuplicatesModalProps {
  isOpen: boolean
  onClose: () => void
  workId: number
  entries: PublicationJournalEntry[]
}

export default function PinterestDuplicatesModal({
  isOpen,
  onClose,
  workId,
  entries,
}: PinterestDuplicatesModalProps) {
  const { confirm } = useConfirm()
  const markDuplicatesMutation = useMarkPinterestDuplicatesDeleted(workId)

  const groups = useMemo(() => groupPinterestDuplicates(entries), [entries])
  const groupsKey = groups.map((g) => g.groupKey).join('|')

  // Set des resource_ids à marquer comme supprimés (pré-sélection : tous
  // sauf le plus ancien de chaque groupe). Reset à chaque ouverture du
  // modal via la `key` posée sur `<Modal>` qui force le remount.
  const [selected, setSelected] = useState<Set<number>>(() =>
    buildInitialDuplicateSelection(groups),
  )

  const handleToggle = (resourceId: number) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(resourceId)) {
        next.delete(resourceId)
      } else {
        next.add(resourceId)
      }
      return next
    })
  }

  const handleSubmit = async () => {
    const resourceIds = Array.from(selected)
    if (resourceIds.length === 0) {
      toast.error('Sélectionne au moins un Pin à marquer comme supprimé.')
      return
    }
    const ok = await confirm({
      title: `Marquer ${resourceIds.length} Pin${resourceIds.length > 1 ? 's' : ''} comme supprimé${resourceIds.length > 1 ? 's' : ''} ?`,
      message:
        'Les Pins sélectionnés seront retirés du journal actif côté atelier. ' +
        'Pinterest ne sera pas appelé — les Pins restent visibles sur pinterest.com ' +
        'tant que tu ne les supprimes pas manuellement là-bas. Le polling analytics ' +
        'PR3 ignorera ces lignes.',
      variant: 'danger',
      confirmText: 'Marquer supprimés',
    })
    if (!ok) return
    try {
      const result = await markDuplicatesMutation.mutateAsync(resourceIds)
      const skipped = result.skipped.length
      const marked = result.marked_deleted_count
      if (skipped > 0) {
        toast.success(
          `${marked} Pin${marked > 1 ? 's' : ''} marqué${marked > 1 ? 's' : ''} supprimé${marked > 1 ? 's' : ''}. ${skipped} ignoré${skipped > 1 ? 's' : ''}.`,
        )
      } else {
        toast.success(
          `${marked} Pin${marked > 1 ? 's' : ''} marqué${marked > 1 ? 's' : ''} supprimé${marked > 1 ? 's' : ''}.`,
        )
      }
      onClose()
    } catch (err) {
      const detail =
        err && typeof err === 'object' && 'detail' in err
          ? String((err as { detail: unknown }).detail)
          : 'Bascule impossible.'
      toast.error(detail)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Détecter doublons Pinterest"
      kicker="Journal des publications"
      size="large"
      key={groupsKey}
    >
      <Modal.Body>
        {groups.length === 0 ? (
          <p className="ui-body ui-text-muted pinterest-duplicates-empty">
            Aucun doublon détecté. On considère doublon : deux Pins Pinterest{' '}
            <code>state=published</code> sur le même board, créés à partir du même draft, dans une
            fenêtre de 60 secondes.
          </p>
        ) : (
          <>
            <p className="ui-body pinterest-duplicates-intro">
              <strong>
                {groups.length} groupe{groups.length > 1 ? 's' : ''} détecté
                {groups.length > 1 ? 's' : ''}.
              </strong>{' '}
              Le plus ancien Pin de chaque groupe est conservé par défaut ; les autres sont
              pré-cochés pour suppression. Tu peux ajuster manuellement avant de valider.
            </p>
            <ul className="pinterest-duplicates-groups" aria-label="Groupes de doublons">
              {groups.map((group) => (
                <DuplicateGroupRow
                  key={group.groupKey}
                  group={group}
                  selected={selected}
                  onToggle={handleToggle}
                />
              ))}
            </ul>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button type="button" variant="ghost" onClick={onClose}>
          Annuler
        </Button>
        <Button
          type="button"
          variant="danger"
          onClick={handleSubmit}
          disabled={selected.size === 0 || markDuplicatesMutation.isPending}
        >
          {markDuplicatesMutation.isPending
            ? 'Suppression…'
            : `Marquer ${selected.size} supprimé${selected.size > 1 ? 's' : ''}`}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

// ──────────────────────────────────────────────────────────────────────────────
// Sub-components
// ──────────────────────────────────────────────────────────────────────────────

interface DuplicateGroupRowProps {
  group: DuplicateGroup
  selected: Set<number>
  onToggle: (resourceId: number) => void
}

function DuplicateGroupRow({ group, selected, onToggle }: DuplicateGroupRowProps) {
  return (
    <li className="pinterest-duplicates-group">
      <header className="pinterest-duplicates-group-header">
        <span className="ui-eyebrow">Board {group.boardId}</span>
        <span className="ui-body-sm ui-text-muted">
          {group.draftLabel} · vers {group.publishedBucket}
        </span>
      </header>
      <ul className="pinterest-duplicates-items">
        {group.items.map((item, index) => (
          <DuplicateItemRow
            key={item.id}
            entry={item}
            isOldest={index === 0}
            checked={selected.has(item.id)}
            onToggle={() => onToggle(item.id)}
          />
        ))}
      </ul>
    </li>
  )
}

interface DuplicateItemRowProps {
  entry: PublicationJournalEntry
  isOldest: boolean
  checked: boolean
  onToggle: () => void
}

function DuplicateItemRow({ entry, isOldest, checked, onToggle }: DuplicateItemRowProps) {
  const publishedAt = entry.published_at
    ? new Date(entry.published_at).toLocaleString('fr-FR', {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })
    : '—'
  const pinId = entry.external_id

  return (
    <li className="pinterest-duplicates-item" data-oldest={isOldest ? 'true' : undefined}>
      <label className="pinterest-duplicates-item-label">
        <Checkbox
          checked={checked}
          onChange={onToggle}
          aria-label={`Marquer le Pin ${pinId} comme supprimé`}
        />
        <span className="pinterest-duplicates-item-text">
          <span className="ui-body">Pin #{pinId}</span>
          <span className="ui-body-sm ui-text-muted"> · publié {publishedAt}</span>
          {isOldest ? (
            <span className="pinterest-duplicates-oldest-tag"> · le plus ancien</span>
          ) : null}
        </span>
      </label>
      {entry.url ? (
        <a
          href={entry.url}
          target="_blank"
          rel="noopener noreferrer"
          className="ui-body-sm pinterest-duplicates-item-link"
        >
          Voir sur Pinterest
        </a>
      ) : null}
    </li>
  )
}
